import { useState, type FormEvent, type ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { api, ErroApi } from '../api/cliente';
import { useSessao } from '../auth/SessaoContext';
import { CabecalhoAuth, MolduraAuth } from './MolduraAuth';
import css from './Auth.module.css';
import { Aviso, Botao, Campo } from '../componentes/ui';

/**
 * Troca obrigatória da senha provisória.
 *
 * Chega aqui quem entrou com uma senha que outra pessoa definiu: o
 * administrador semeado pelo `.env` no primeiro acesso, ou um cliente cuja
 * senha o suporte redefiniu. Até concluir, a API recusa qualquer outra rota
 * com 403 — a tela não é a barreira, só o caminho para sair dela.
 */
export function TrocarSenha() {
  const { usuario, sair } = useSessao();
  const navegar = useNavigate();

  const [senhaAtual, setSenhaAtual] = useState('');
  const [senhaNova, setSenhaNova] = useState('');
  const [confirmacao, setConfirmacao] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [salvando, setSalvando] = useState(false);
  const [pronto, setPronto] = useState(false);

  const curta = senhaNova.length > 0 && senhaNova.length < 10;
  const semNumero = senhaNova.length > 0 && !/\d/.test(senhaNova);
  const semLetra = senhaNova.length > 0 && !/[A-Za-z]/.test(senhaNova);
  const divergem = confirmacao.length > 0 && confirmacao !== senhaNova;
  const invalida = curta || semNumero || semLetra;

  async function enviar(e: FormEvent) {
    e.preventDefault();
    if (invalida || divergem || !senhaAtual) return;

    setErro(null);
    setSalvando(true);
    try {
      await api<void>('/auth/senha', {
        metodo: 'PATCH',
        corpo: { senhaAtual, senhaNova },
      });
      // A troca revoga TODAS as sessões, inclusive esta — não dá para seguir
      // direto ao painel. Mandar ao login também prova, na prática, que a senha
      // nova funciona, antes de a pessoa fechar a aba achando que terminou.
      setPronto(true);
    } catch (err) {
      setErro(
        err instanceof ErroApi
          ? err.message
          : 'Não foi possível trocar a senha.',
      );
      setSalvando(false);
    }
  }

  if (pronto) {
    return (
      <Moldura
        titulo="Senha definida"
        subtitulo="Entre novamente com a senha que você acabou de escolher."
      >
        <Aviso tom="ok">
          <span>
            As sessões abertas foram encerradas, inclusive esta — é o que
            garante que a senha antiga não vale mais em lugar nenhum.
          </span>
        </Aviso>
        <Botao
          larguraTotal
          onClick={() => navegar('/entrar', { replace: true })}
        >
          Ir para o login
        </Botao>
      </Moldura>
    );
  }

  return (
    <Moldura
      titulo="Defina sua senha"
      subtitulo="Esta senha foi criada por outra pessoa e só vale para este primeiro acesso."
    >
      {usuario && (
        <div className={css.identidade}>
          Entrando como <b>{usuario.email}</b>
        </div>
      )}

      {erro && <Aviso>{erro}</Aviso>}

      <form onSubmit={enviar} noValidate className={css.campos}>
        <Campo
          rotulo="Senha atual"
          type="password"
          autoComplete="current-password"
          value={senhaAtual}
          onChange={(e) => setSenhaAtual(e.target.value)}
          dica="A que você recebeu."
        />
        <Campo
          rotulo="Nova senha"
          type="password"
          autoComplete="new-password"
          value={senhaNova}
          onChange={(e) => setSenhaNova(e.target.value)}
          erro={
            curta
              ? 'Use ao menos 10 caracteres.'
              : semLetra
                ? 'Inclua ao menos uma letra.'
                : semNumero
                  ? 'Inclua ao menos um número.'
                  : undefined
          }
          dica="Ao menos 10 caracteres, com letras e números."
        />
        <Campo
          rotulo="Confirme a nova senha"
          type="password"
          autoComplete="new-password"
          value={confirmacao}
          onChange={(e) => setConfirmacao(e.target.value)}
          erro={divergem ? 'As senhas não conferem.' : undefined}
        />

        <Botao
          type="submit"
          larguraTotal
          grande
          carregando={salvando}
          disabled={!senhaAtual || !senhaNova || invalida || divergem}
        >
          Definir senha e continuar
        </Botao>
      </form>

      <Botao
        aparencia="discreto"
        larguraTotal
        onClick={() => {
          void sair().then(() => navegar('/entrar', { replace: true }));
        }}
      >
        Sair
      </Botao>
    </Moldura>
  );
}

function Moldura({
  titulo,
  subtitulo,
  children,
}: {
  titulo: string;
  subtitulo: string;
  children: ReactNode;
}) {
  return (
    <MolduraAuth>
      <div className={css.pilha}>
        <CabecalhoAuth titulo={titulo}>{subtitulo}</CabecalhoAuth>
        {children}
      </div>
    </MolduraAuth>
  );
}
